import { useEffect } from "react";
import {
  UseQueryOptions,
  UseQueryResult,
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import { Temporal } from "temporal-polyfill";

import { api, ApiResponse, indexById, type PageResult } from "_/utils";
import { Uuid, JsonValue } from "_/types";
import { type Buildset } from "_/data/buildsets";
import { type Image } from "_/data/images";
import { Org, useCurrentOrg } from "_/data/orgs";

const MACHINE_CACHE_TIME = 10 * 1000; // 10 seconds

const SCOPE = "machines";
const KEYS = {
  all: () => [SCOPE],
  machine: (id: Uuid) => [SCOPE, "detail", id],
  org: (orgId: Uuid) => [SCOPE, "org", orgId],
  events: ({ machineId, from, to, kind, limit, order }: EventQuery) => [
    SCOPE,
    "events",
    machineId,
    from,
    to,
    kind,
    limit,
    order,
  ],
  images: ({ machineId, from, to, limit }: MachineImageQuery) => [
    SCOPE,
    "images",
    machineId,
    from,
    to,
    limit,
  ],
  tempRange: (id: Uuid) => [SCOPE, "tempRange", id],
  snapshot: (id: Uuid) => [SCOPE, id, "snapshot"],
  buildsetHistory: (id: Uuid) => [SCOPE, id, "buildsetHistory"],
  logs: (q: LogQuery) => [SCOPE, "logs", q],
};

export type Hardware = {
  /**
   * Canonical machine ID.
   */
  id: Uuid;

  /**
   * Machine serial number.
   */
  serial: string;

  /**
   * Date of original manufacture, as a string like 2025-02-01.
   */
  manufactureDate: string;

  /**
   * Machine generation.
   *
   * The machine generation is the most general version specifier for machines and maps to the
   * base architecture used - it can be thought of similar to a semver major. This starts at 2
   * with M2-2018, M2-2020, and M2+ generation machines.
   */
  generation: number;

  /**
   * Machine model name (e.g. M2, M2+ etc.)
   */
  model: string;
};

export type Machine = {
  /**
   * Canonical machine ID.
   */
  id: Uuid;

  /**
   * Hardware backing organization machine instance.
   */
  hardware: Hardware;

  /**
   * Indicates whether or not the hardware is currently granted to this machine.
   */
  hardwareAttached: boolean;

  /**
   * Each machine is given its own domain ID which is scoped under another parent domain.
   */
  domainId: Uuid;

  /**
   * Owning organization ID.
   */
  orgId: Uuid;

  /**
   * Human readable name.
   */
  name: string;

  /**
   * Record creation timestamp.
   */
  createdAt: string;

  /**
   * Record update timestamp.
   */
  updatedAt: string;

  /**
   * Last time that the machine was "seen" by the Basis backend.
   */
  lastSeen: string | null;

  /**
   * Latest status of this machine.
   */
  status: MachineStatus;

  /**
   * Machine wired ipv4 LAN address.
   */
  lanAddress?: string;

  /**
   * Machine wireless ipv4 LAN address.
   */
  wlanAddress?: string;

  /**
   * Software release channel for the machine to track.
   */
  updateChannel: string;

  /**
   * Pinned target buildset.
   *
   * If not set, the machine will target the channel buildset.
   */
  buildsetPin: string | null;

  /**
   * Reason for pinning this machine to a specific software version.
   *
   * Only meaningful when buildsetPin is set.
   */
  pinReason: string | null;

  /**
   * Arbitrary machine metadata.
   *
   * This can be used to store data such as references to external services (e.g. hubspot or
   * balena) and other meta information which should be included.
   */
  metadata?: Record<string, string | number | boolean | null>;
};

export const LOG_LEVEL_NAMES = ["debug", "info", "warn", "error"] as const;
export type LogLevel = (typeof LOG_LEVEL_NAMES)[number];

export type LogEvent = {
  machineId: Uuid;
  level: LogLevel;
  timestamp: number;
  component: string;
  message: string;
  data: Record<string, unknown>;
};

async function getMachine(id: Uuid): Promise<Machine | undefined> {
  const response = await api.get<ApiResponse<Machine>>(`/machines/${id}`);
  return response.data.data;
}

export function useMachine(
  id: Uuid,
  options: Omit<
    UseQueryOptions<Machine | undefined>,
    "queryKey" | "queryFn"
  > = {}
) {
  return useQuery({
    ...options,
    queryKey: KEYS.machine(id),
    queryFn: () => getMachine(id),
    enabled: (query) =>
      Boolean(id) &&
      (typeof options.enabled === "function"
        ? options.enabled(query)
        : (options.enabled ?? true)),
  });
}

async function getAllMachines(): Promise<Machine[]> {
  const response = await api.get<ApiResponse<Machine[]>>("/machines");
  return response.data.data;
}

/**
 * Get machines in all orgs.
 *
 * The caller must have the `machines/read` permission in the root domain.
 */
export function useAllMachines() {
  return useQuery({
    queryKey: KEYS.all(),
    queryFn: getAllMachines,
    gcTime: MACHINE_CACHE_TIME,
  });
}

/**
 * Get machines from multiple organizations.
 */
export function useMachinesByOrgs(orgs?: Org[]) {
  const orgsById = indexById(orgs || []);

  let queryFn: () => Promise<Machine[]>;
  let queryKey: string[];
  if (!orgs?.length) {
    // Using an async function here to align with other branches.
    // oxlint-disable-next-line require-await
    queryFn = async () => [];
    queryKey = [SCOPE, "empty"]; // will be ignored; no need to be unique
  } else if (orgs?.length === 1) {
    // If only one org, use the one-org endpoint `/organizations/:id/machines`
    // which has more lenient permissions on the server so that single-org
    // admins can call it. For multiple org callers (currently only the Factory
    // page needs this), we'll use the `/machines` endpoint which requires
    // permissions in the root domain.
    const orgId = orgs[0].id;
    queryFn = () => getMachinesByOrg({ orgId });
    queryKey = KEYS.org(orgId);
  } else {
    queryFn = getAllMachines;
    queryKey = KEYS.all();
  }

  return useQuery({
    queryKey,
    queryFn,
    gcTime: MACHINE_CACHE_TIME,
    select: (data: Machine[]) => {
      return data
        .filter((machine) => orgsById[machine.orgId])
        .map((machine) => ({ org: orgsById[machine.orgId], machine }));
    },
  });
}
/**
 * Get machines in the current organization.
 */
export function useMachines() {
  const { data: currentOrg } = useCurrentOrg();

  return useQuery({
    queryKey: KEYS.org(currentOrg.id),
    queryFn: () => getMachinesByOrg({ orgId: currentOrg.id }),
    gcTime: MACHINE_CACHE_TIME,
  });
}

/**
 * Get the machines in a specific organization by ID.
 */
async function getMachinesByOrg({
  orgId,
}: {
  orgId: Uuid;
}): Promise<Machine[]> {
  const response = await api.get<ApiResponse<Machine[]>>(
    `/organizations/${orgId}/machines`
  );

  return response.data.data;
}

export interface NewMachine {
  /** Org ID of org where the new machine should be created */
  orgId: Uuid;
  /** Machine serial number */
  serial: string;
  updateChannel?: string;
}

/**
 * Mutation for creating a new machine in the Factory org (which is the only
 * place that new machines should be created).
 */
export function useCreateMachine() {
  async function createMachine(params: {
    newMachine: NewMachine;
    onError?: ({
      statusCode,
      message,
    }: {
      statusCode?: number;
      message?: string;
    }) => void;
    onSuccess?: (machine: Machine) => void;
  }): Promise<Machine> {
    const response = await api.post<ApiResponse<Machine>>(
      `/machines`,
      params.newMachine
    );
    return response.data.data;
  }

  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createMachine,
    onSuccess: (data: Machine, { onSuccess }) => {
      void queryClient.invalidateQueries({ queryKey: KEYS.all() });
      onSuccess?.(data);
    },
    onError: (e: AxiosError<{ message?: string }>, { onError }) => {
      onError?.({
        statusCode: e.response?.status,
        message: e.response?.data?.message,
      });
    },
  });
}

/**
 * Mutation for transferring hardware from one organization to another.
 */
export function useTransferHardware() {
  async function transferHardware(params: {
    orgId: Uuid;
    machineId: Uuid;
    onError?: ({
      statusCode,
      message,
    }: {
      statusCode?: number;
      message?: string;
    }) => void;
    onSuccess?: (machine: Machine) => void;
  }): Promise<Machine> {
    const response = await api.post<ApiResponse<Machine>>(
      `/machines/${params.machineId}/transfer`,
      { orgId: params.orgId }
    );

    return response.data.data;
  }

  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: transferHardware,
    onSuccess: (data: Machine, { onSuccess }) => {
      void queryClient.invalidateQueries({ queryKey: KEYS.all() });
      onSuccess?.(data);
    },
    onError: (e: AxiosError<{ message?: string }>, { onError }) => {
      onError?.({
        statusCode: e.response?.status,
        message: e.response?.data?.message,
      });
    },
  });
}

/**
 * Mutation for renaming machines.
 *
 * Note that this name is only for display. It doesn't, for example, change the
 * name that the machine registers itself with DHCP.
 */
export interface MachinePatch {
  name?: string;
  updateChannel?: string;
  buildsetPin?: string | null;
  pinReason?: string;
}

export function useEditMachine() {
  async function editMachine(data: {
    id: Uuid;
    params: MachinePatch;
    onError?: ({
      statusCode,
      message,
    }: {
      statusCode?: number;
      message?: string;
    }) => void;
    onSuccess?: (machine: Machine) => void;
  }): Promise<Machine> {
    const response = await api.patch<ApiResponse<Machine>>(
      `/machines/${data.id}`,
      data.params
    );

    return response.data.data;
  }

  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: editMachine,
    onSuccess: (data: Machine, { onSuccess }) => {
      void queryClient.invalidateQueries({ queryKey: KEYS.all() });
      onSuccess?.(data);
    },
    onError: (e: AxiosError<{ message?: string }>, { onError }) => {
      onError?.({
        statusCode: e.response?.status,
        message: e.response?.data?.message,
      });
    },
  });
}

export type ThermalsDataCleaned = {
  t0Temperature: number[];
  t0Target: number[];
  t1Temperature: number[];
  t1Target: number[];
  bedTemperature: number[];
  bedTarget: number[];
  chamberTemperature: number[];
  chamberTarget: number[];
  timestamp: number[];
};

async function getMachineThermals(
  machineId: Uuid,
  // in microseconds
  from: number,
  // in microseconds
  to?: number
) {
  const path = to
    ? `/machines/${machineId}/events?from=${from}&to=${to}&kind=thermals&order=asc`
    : `/machines/${machineId}/events?from=${from}&kind=thermals&order=asc`;
  const response = await api.get<ApiResponse<ThermalEvent[]>>(path);
  return response.data.data;
}

/**
 * Reformat the data from the API into a format that is easier to use in a chart.
 */
function cleanThermalsData(
  newData: ThermalEvent[],
  oldData?: ThermalsDataCleaned
) {
  const cleanedData: ThermalsDataCleaned = oldData || {
    t0Temperature: [],
    t0Target: [],
    t1Temperature: [],
    t1Target: [],
    bedTemperature: [],
    bedTarget: [],
    chamberTemperature: [],
    chamberTarget: [],
    timestamp: [],
  };

  newData.forEach((event) => {
    const { t0, t1, bed, chamber } = event.data;
    cleanedData.t0Temperature.push(t0.temperature);
    cleanedData.t0Target.push(t0.target);
    cleanedData.t1Temperature.push(t1.temperature);
    cleanedData.t1Target.push(t1.target);
    cleanedData.bedTemperature.push(bed.temperature);
    cleanedData.bedTarget.push(bed.target);
    cleanedData.chamberTemperature.push(chamber.temperature);
    cleanedData.chamberTarget.push(chamber.target);
    cleanedData.timestamp.push(event.timestamp);
  });

  return cleanedData;
}

export function useMachineThermals(
  machine: Machine,
  onData?: (data: ThermalsDataCleaned) => void
) {
  const queryClient = useQueryClient();
  const queryKey = KEYS.tempRange(machine.id);

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const oldData: ThermalsDataCleaned | undefined =
        queryClient.getQueryData(queryKey) || undefined;

      // Derive `from` from the cache's last timestamp so remounting the component
      // correctly resumes from where the cache left off. A useRef would reset to null
      // on remount, causing the fetch to restart from 1 hour ago and duplicate data.
      const from =
        oldData?.timestamp.at(-1) ?? (Date.now() - 60 * 60 * 1000) * 1000;
      const newData = await getMachineThermals(machine.id, from);

      return cleanThermalsData(newData, oldData);
    },
    placeholderData: keepPreviousData,
    refetchInterval: 2000,
    // When a callback is provided, only re-render when dataUpdatedAt changes (each refetch),
    // not on every query state change. query.data is mutated in place so its reference never
    // changes, making it useless as a useEffect dependency.
    notifyOnChangeProps: onData ? ["dataUpdatedAt"] : undefined,
  });

  useEffect(() => {
    if (onData && query.data) {
      onData(query.data);
    }
  }, [onData, query.data, query.dataUpdatedAt]);

  return query;
}

/**
 * Utility type to define the different kinds of telemetry events, and their
 * kind (a string) and payload.
 * */
type TelemetryEventKindAndData =
  | { kind: "thermals"; data: ThermalsEventData }
  | { kind: "stateChange"; data: StateChangeEventData }
  | { kind: "startup"; data: StartupEventData }
  | { kind: "systemInfo"; data: SystemInfoEventData }
  | { kind: "systemProfile"; data: SystemProfileEventData }
  | { kind: "snapshot"; data: SnapshotEventData };

export type TelemetryEvent<K extends TelemetryEventKind = TelemetryEventKind> =
  {
    timestamp: number;
    kind: K;
  } & TelemetryEventKindAndData;

export type TelemetryEventKind = TelemetryEventKindAndData["kind"];

export interface StartupEventData {
  networkInfo: {
    lanAddress: string | null;
    wlanAddress: string | null;
  };
  agentVersion: string;
}

/** Information about a disk partition. */
export interface DiskInfo {
  /** Device name, e.g. "/dev/sda1" */
  device: string;

  /** Mountpoint, e.g. "/secrets" */
  mountpoint: string;

  /** Size of the partition, in bytes */
  size: number;

  /** Free space percentage from 0-100 */
  freePct: number;
}

/** Represents the version for an AON3D software component. */
export interface ComponentVersion {
  /** The name of the component. */
  component: string;

  /**
   * Version of this component that's awaiting download. Note that this is
   * not populated yet so may be an empty string.
   */
  pending: string;

  /** The version of the component in the currently-installed buildset. */
  expected: string;

  /** The actual current version of the component. */
  actual: string;
}

/**
 * Machine configuration data for diagnostic purposes.
 *
 * This event data contains infrequently-changing information about the machine
 * such as hostname, IP addresses, or time zone. Agent will send this at startup
 * and whenever this information changes.
 *
 * Agent may add additional fields over time as we discover more configuration
 * problems to track.
 *
 * Note that this is distinct from {@link StartupEventData}, which is only sent
 * once at startup and contains only a subset of this data. Once all field
 * machines are updated to a modern Agent version, we may deprecate some of the
 * fields in {@link StartupEventData}.
 */
export interface SystemInfoEventData {
  /** AON3D software component versions. */
  components?: ComponentVersion[];

  /** Buildset ID string, if available. */
  buildsetId?: Uuid;

  /** Wireless network interface v4 address. */
  wlanAddress?: string;

  /** Wired network interface v4 address. */
  lanAddress?: string;

  /** Time the machine was last booted, as an RFC 3339 string. */
  bootTime: string;

  /** Time zone of the machine, e.g. "America/Los_Angeles" */
  timeZone?: string;

  /** Hostname of the machine. */
  hostname?: string;

  /**
   * Information about disk partitions, primarily to track
   * partition-full conditions.
   */
  diskUsage: DiskInfo[];

  /** Additional fields not explicitly defined above. */
  [key: string]: unknown;
}

type MachineTemperatureItem = {
  temperature: number;
  target: number;
};

export type ThermalsEventData = {
  t0: MachineTemperatureItem;
  t1: MachineTemperatureItem;
  bed: MachineTemperatureItem;
  chamber: MachineTemperatureItem;
};

export type SystemProfileEventData = {
  /** CPU usage percentage. */
  cpu: number | null;

  /** Memory usage percentage. */
  mem: number | null;

  /** Disk usage percentage. */
  disk: number | null;
};

export type SnapshotEventData = Record<string, unknown>;

/** Motion data sent to Basis */
export type MotionDataForBasis = readonly [
  number, // timestamp
  [number, number, number], // XYZ position
  readonly [0, 0 | 1 | -1] | readonly [0 | 1 | -1, 0], // extrusion direction per tool
  0 | 1, // current tool
];

/** Encoder drift data to compare expected position to actual position */
export type EncoderDataForBasis = [
  number, // timestamp
  [number, number], // ???
  [number, number], // ???
];

/**
 * Tool force sensor data samples coming from Klipper are arrays of where the first element
 * is the timestamp, and the rest are the values from the sensor.
 */
type SensorDataSample = number[];

export type SensorEventData =
  | {
      sensor: "motionData";
      elements: MotionDataForBasis[];
    }
  | {
      sensor: "axesEncoders";
      elements: EncoderDataForBasis[];
    }
  | {
      sensor: "toolForce0" | "toolForce1";
      elements: SensorDataSample[];
    };

/**
 * The possible states of a machine.
 *
 * Note that "unknown" is not sent by the server. Instead, it's used by the
 * client to indicate when we don't have info from the server about the status
 * of this machine.
 */
export type MachineStatus = StateChangeEventKind;

type StateChangeEventKind =
  | "standby"
  | "printing"
  | "paused"
  | "complete"
  | "cancelled"
  | "error"
  | "unknown";
export interface StateChangeEventData {
  state: MachineStatus;
  message?: string | null;
  fileName?: string | null;
  revisionId?: Uuid | null;
  blobId?: Uuid | null;
}

type ThermalEvent = TelemetryEvent<"thermals">;
export type StartupEvent = TelemetryEvent<"startup">;
export type SystemInfoEvent = TelemetryEvent<"systemInfo">;
export type StateChangeEvent = TelemetryEvent<"stateChange">;
export type SnapshotEvent = TelemetryEvent<"snapshot">;

export type EventQuery = {
  /** Machine ID to query */
  machineId: Uuid;
  /** Epoch microseconds for start of the query range (i.e. oldest event). */
  from?: number;
  /** Epoch microseconds for end of the query range (i.e. newest event). */
  to?: number;
  /** Kind(s) of events to return. If an empty array, then all kinds will be returned. */
  kind?: TelemetryEventKind[];
  /** Maximum number of events to return */
  limit?: number;
  /** Order in which to return events */
  order?: "asc" | "desc";
};

type UseMachineEventsOptions<
  T extends TelemetryEventKind,
  TSelected = TelemetryEvent<T>[],
> = Omit<
  UseQueryOptions<
    TelemetryEvent<T>[],
    unknown,
    TSelected,
    ReturnType<typeof KEYS.events>
  >,
  "queryKey" | "queryFn"
>;

export function useMachineEvents<
  T extends TelemetryEventKind,
  TSelected = TelemetryEvent<T>[],
>(
  query: EventQuery & { kind: [T] },
  configOptions?: UseMachineEventsOptions<T, TSelected>
): UseQueryResult<TSelected>;
export function useMachineEvents<TSelected = TelemetryEvent[]>(
  query: EventQuery,
  configOptions?: UseMachineEventsOptions<TelemetryEventKind, TSelected>
): UseQueryResult<TSelected>;
export function useMachineEvents<
  T extends TelemetryEventKind = TelemetryEventKind,
  TSelected = TelemetryEvent<T>[],
>(query: EventQuery, configOptions?: UseMachineEventsOptions<T, TSelected>) {
  async function getMachineEvents() {
    const from = query.from;
    const to = query.to;
    const limit = query.limit || 1_000;
    const kind = query.kind || [];
    const order = query.order;

    const path = [
      `/machines/${query.machineId}/events`,
      limit ? `?limit=${limit}` : "",
      from ? `&from=${from}` : "",
      to ? `&to=${to}` : "",
      kind.map((k, i) => `&kind[${i}]=${k}`).join(""),
      order ? `&order=${order}` : "",
    ].join("");

    const response = await api.get<ApiResponse<TelemetryEvent<T>[]>>(path);

    return response.data.data;
  }

  return useQuery({
    queryKey: KEYS.events(query),
    queryFn: getMachineEvents,
    ...configOptions,
  });
}

/** A camera image captured by a machine. */
export type MachineImage = {
  image: Image;
  machineId: Uuid;
  /** Attempt running when the image was captured, if any. */
  attemptId: Uuid | null;
  camera: "purge_cam" | "chamber_cam_0";
  /** Epoch microseconds when the image was captured. */
  capturedAt: number;
};

type MachineImageQuery = {
  /** Machine ID to query */
  machineId: Uuid;
  /** Epoch microseconds for start of the query range (i.e. oldest image). */
  from?: number;
  /** Epoch microseconds for end of the query range (i.e. newest image). */
  to?: number;
  /** Maximum number of images to return, most recent first */
  limit?: number;
};

/** Format epoch microseconds as an RFC 3339 timestamp, keeping microsecond precision. */
const microsecondsToTimestamp = (microseconds: number) =>
  Temporal.Instant.fromEpochNanoseconds(
    BigInt(Math.trunc(microseconds)) * 1_000n
  ).toString();

export function useMachineImages(
  query: MachineImageQuery,
  configOptions?: Omit<
    UseQueryOptions<
      MachineImage[],
      unknown,
      MachineImage[],
      ReturnType<typeof KEYS.images>
    >,
    "queryKey" | "queryFn"
  >
) {
  async function getMachineImages() {
    const from =
      query.from !== undefined
        ? microsecondsToTimestamp(query.from)
        : undefined;
    const to =
      query.to !== undefined ? microsecondsToTimestamp(query.to) : undefined;

    const response = await api.get<PageResult<MachineImage>>(
      `/machines/${query.machineId}/images`,
      {
        params: {
          limit: query.limit ?? 1_000,
          from,
          to,
          // The server's page cursor defaults to now, which would leave out
          // images from machines whose clock runs ahead.
          before: to,
        },
      }
    );

    return response.data.data.items;
  }

  return useQuery({
    queryKey: KEYS.images(query),
    queryFn: getMachineImages,
    ...configOptions,
  });
}

export type LogQuery = {
  /** Machine ID to query */
  machineIds: Uuid[];
  /** Epoch microseconds for start of the query range (i.e. oldest event) */
  from: string;
  /** Epoch microseconds for end of the query range (i.e. newest event) */
  to: string;
  /** Level(s) of events to return. If an empty array, then all levels will be returned. */
  levels?: LogLevel[];
  /** Text to search for (case-insensitively) in the event message */
  text?: string;
  /** Event source(s), e.g. ["Fluidd", "Klipper"]. Matching is case insensitive. */
  components?: string[];
  /**
   * Filter(s) on the `data` field(s) of the event. Each filter is a json path
   * expression that will be passed directly to Postgres, e.g. $.foo.bar==42.
   * Unlike all other filters, data filters are case-sensitive because it's not
   * worth the work to make them case-insensitive. When there are multiple data
   * filters, all of them must be satisfied (they are logically AND-ed).
   */
  data?: string[];
  /** Maximum number of events to return */
  limit?: number;
  /** Order in which to return events */
  order?: "asc" | "desc";
};

export function useMachineLogs(query: LogQuery) {
  const {
    limit,
    machineIds,
    from,
    to,
    order,
    text,
    levels = ["info", "warn", "error"],
    components,
    data,
  } = query;

  async function getMachineLogs(): Promise<LogEvent[]> {
    // We are only using the first machine ID in the array for now since we
    // only query logs for one machine at a time. If we ever need to query
    // logs for multiple machines, we can/will modify this for use with
    // a generalized endpoint.
    const path = [
      `/machines/${machineIds[0]}/logs?`,
      limit ? `&limit=${limit}` : "",
      from ? `&from=${from}` : "",
      to ? `&to=${to}` : "",
      text ? `&text=${encodeURIComponent(text)}` : "",
      levels.map((level, i) => `&levels[${i}]=${level}`).join(""),
      order ? `&order=${order}` : "",
      components
        ? components
            .map((c, i) => `&components[${i}]=${encodeURIComponent(c)}`)
            .join("")
        : "",
      data
        ? data.map((d, i) => `&data[${i}]=${encodeURIComponent(d)}`).join("")
        : "",
    ].join("");
    const response = await api.get<ApiResponse<LogEvent[]>>(path);
    return response.data.data;
  }

  return useQuery({
    queryKey: KEYS.logs(query),
    queryFn: getMachineLogs,
    refetchOnWindowFocus: false,
    gcTime: 5 * 1000,
    staleTime: 5 * 1000,
  });
}

export type BuildsetHistoryEntry = Omit<Buildset, "targetMachines"> & {
  /** When the machine first reported running this buildset. */
  timestamp: string;
};

export function useMachineBuildsetHistory(machineId: Uuid) {
  async function getMachineBuildsetHistory(): Promise<BuildsetHistoryEntry[]> {
    const response = await api.get<ApiResponse<BuildsetHistoryEntry[]>>(
      `/machines/${machineId}/buildsets`
    );
    return response.data.data;
  }

  return useQuery({
    queryKey: KEYS.buildsetHistory(machineId),
    queryFn: getMachineBuildsetHistory,
    refetchInterval: MACHINE_CACHE_TIME,
  });
}

export function useMachineSnapshot(machineId: Uuid) {
  async function getMachineSnapshot(
    id: Uuid
  ): Promise<{ snapshot: JsonValue; timestamp: number }> {
    const response = await api.get<
      ApiResponse<{ snapshot: JsonValue; timestamp: number }>
    >(`/machines/${id}/snapshot`);

    return response.data.data;
  }

  return useQuery({
    queryKey: KEYS.snapshot(machineId),
    queryFn: () => getMachineSnapshot(machineId),
  });
}

type DeviceAuthGrant = {
  deviceCode: string;
  userCode: string;
  hardwareId: Uuid;
  interval: number;
  expiresIn: number;
  authorizedMachine?: Uuid;
};

export function useMachineAuthorize() {
  async function authorizeMachine(params: {
    userCode: string;
    orgId: Uuid;
  }): Promise<DeviceAuthGrant> {
    const response = await api.post<ApiResponse<DeviceAuthGrant>>(
      "/auth/device/authorize",
      params
    );

    return response.data.data;
  }

  return useMutation({ mutationFn: authorizeMachine });
}
